import React, { FC, useTransition } from 'react';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import getClassNames from '../../utils/getClassNames';
import type { GhostButtonSize } from '../ButtonDS22';

import styles from './BackButton.module.scss';
import type { IconProps } from '../IconDS22';
import Icon from '../IconDS22';

export type BackButtonProps = {
  onClick?: () => void;
  tabIndex?: number;
  size?: GhostButtonSize;
  position?: 'absolute' | 'static';
  label?: string;
  ariaLabel?: string;
  dataCwv?: string;
  dataTest?: string;
};

/** Size of the Icon and LoadingSpinner is coupled to ButtonSize */
const sizeMap: {
  iconGhost: Record<GhostButtonSize, IconProps['size']>;
} = {
  // The Ghost variant has different icon size mapping
  iconGhost: {
    small: 's',
    medium: 'm',
    large: 'l',
    'x-large': 'xl',
  },
};

const BackButton: FC<BackButtonProps> = (
  {
    onClick,
    tabIndex = 0,
    size = 'medium',
    position = 'absolute',
    label,
    ariaLabel,
    dataCwv = 'back',
    dataTest,
  },
) => {
  const { t } = useTranslation('common');
  const [,startTransition] = useTransition();
  const deferredOnClick = () => startTransition(() => onClick?.());

  return (
    <div className={getClassNames(styles, 'root', classNames(position))}>
      <button
        className={getClassNames(styles, 'button', classNames(size))}
        type="button"
        onClick={deferredOnClick}
        aria-label={ariaLabel || t('back-button-label')}
        tabIndex={tabIndex}
        data-cwv={dataCwv}
        data-test={dataTest}
      >
        <span className={getClassNames(styles, 'icon', classNames(size))}>
          <Icon id="caret-left" size={sizeMap.iconGhost[size]} />
        </span>
        {label}
      </button>
    </div>
  );
};

export default BackButton;
